/**
 * Company Profile Management Page
 *
 * Allows company users to view and edit their company profile information:
 * - Company details (name, registration, tax ID)
 * - Contact information
 * - Business address
 * - Industry and business type
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import { Alert } from '../../components/common/Alert';
import { mockApi } from '../../services/mockApi';
import type { Company } from '../../types/organization';
import { IndustrySector } from '../../types/organization';

export const CompanyProfilePage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState<Partial<Company>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadCompanyProfile();
  }, []);

  const loadCompanyProfile = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const response = await mockApi.getOrganization('org-001'); // Mock company ID
      if (response.success && response.data) {
        const companyData = response.data as Company;
        setCompany(companyData);
        setFormData(companyData as Partial<Company>);
      } else {
        setErrorMessage(response.error || 'Failed to load company profile');
      }
    } catch (error) {
      console.error('Error loading company profile:', error);
      setErrorMessage('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form to original data
      setFormData(company || {});
      setSuccessMessage('');
      setErrorMessage('');
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Mock API call - in real implementation, this would update the backend
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Update local state
      setCompany(formData as Company);
      setSuccessMessage('Company profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving company profile:', error);
      setErrorMessage('Failed to save company profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedFieldChange = (
    parent: 'primaryContact' | 'businessAddress',
    field: string,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent] || {}),
        [field]: value,
      },
    }));
  };

  const industryOptions = [
    { value: IndustrySector.FOOD_MANUFACTURING, label: 'Food Manufacturing' },
    { value: IndustrySector.FOOD_SERVICE, label: 'Food Service' },
    { value: IndustrySector.LOGISTICS, label: 'Logistics' },
    { value: IndustrySector.PACKAGING, label: 'Packaging' },
    { value: IndustrySector.INGREDIENTS, label: 'Ingredients' },
    { value: IndustrySector.RETAIL, label: 'Retail' },
    { value: IndustrySector.PHARMACEUTICALS, label: 'Pharmaceuticals' },
    { value: IndustrySector.COSMETICS, label: 'Cosmetics' },
    { value: IndustrySector.OTHER, label: 'Other' },
  ];

  if (loading) {
    return (
      <PageLayout pageTitle={t('company:profile.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common:status.loading')}...</div>
        </div>
      </PageLayout>
    );
  }

  if (!company) {
    return (
      <PageLayout pageTitle={t('company:profile.title')}>
        <Alert type="error" title="Error">
          {errorMessage || 'Company profile not found'}
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={t('company:profile.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:profile.title') },
      ]}
      actions={
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleEditToggle} disabled={saving}>
                {t('common:actions.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSave} isLoading={saving}>
                {t('common:actions.save')}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={handleEditToggle}>
              {t('common:actions.edit')}
            </Button>
          )}
        </div>
      }
    >
      {successMessage && (
        <Alert type="success" title="Success" dismissible onDismiss={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert type="error" title="Error" dismissible onDismiss={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Company Details */}
      <Card className="mb-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('company:profile.companyDetails')}</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t('company:profile.companyName')}
              value={formData.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Select
              label={t('company:profile.industry')}
              value={formData.industry || ''}
              options={industryOptions}
              onChange={(e) => handleFieldChange('industry', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label={t('company:profile.registrationNumber')}
              value={formData.registrationNumber || ''}
              onChange={(e) => handleFieldChange('registrationNumber', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label={t('company:profile.taxId')}
              value={formData.taxId || ''}
              onChange={(e) => handleFieldChange('taxId', e.target.value)}
              disabled={!isEditing}
            />

            <Input
              label={t('company:profile.website')}
              type="url"
              value={formData.website || ''}
              onChange={(e) => handleFieldChange('website', e.target.value)}
              disabled={!isEditing}
            />

            <Input
              label={t('company:profile.yearEstablished')}
              type="number"
              value={formData.yearEstablished || ''}
              onChange={(e) => handleFieldChange('yearEstablished', parseInt(e.target.value, 10))}
              disabled={!isEditing}
              required
            />

            <Input
              label={t('company:profile.numberOfEmployees')}
              type="number"
              value={formData.numberOfEmployees || ''}
              onChange={(e) => handleFieldChange('numberOfEmployees', parseInt(e.target.value, 10))}
              disabled={!isEditing}
            />
          </div>
        </CardBody>
      </Card>

      {/* Contact Person */}
      <Card className="mb-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('company:profile.contactPerson')}</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t('common:common.name')}
              value={formData.primaryContact?.name || ''}
              onChange={(e) => handleNestedFieldChange('primaryContact', 'name', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label="Position"
              value={formData.primaryContact?.position || ''}
              onChange={(e) => handleNestedFieldChange('primaryContact', 'position', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label={t('company:profile.contactEmail')}
              type="email"
              value={formData.primaryContact?.email || ''}
              onChange={(e) => handleNestedFieldChange('primaryContact', 'email', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label={t('company:profile.contactPhone')}
              type="tel"
              value={formData.primaryContact?.phone || ''}
              onChange={(e) => handleNestedFieldChange('primaryContact', 'phone', e.target.value)}
              disabled={!isEditing}
              required
            />

            <Input
              label="Mobile"
              type="tel"
              value={formData.primaryContact?.mobile || ''}
              onChange={(e) => handleNestedFieldChange('primaryContact', 'mobile', e.target.value)}
              disabled={!isEditing}
            />
          </div>
        </CardBody>
      </Card>

      {/* Business Address */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('company:profile.businessAddress')}</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 gap-6">
            <Input
              label="Street Address"
              value={formData.businessAddress?.street || ''}
              onChange={(e) => handleNestedFieldChange('businessAddress', 'street', e.target.value)}
              disabled={!isEditing}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                label={t('common:common.city')}
                value={formData.businessAddress?.city || ''}
                onChange={(e) => handleNestedFieldChange('businessAddress', 'city', e.target.value)}
                disabled={!isEditing}
                required
              />

              <Input
                label={t('common:common.state')}
                value={formData.businessAddress?.state || ''}
                onChange={(e) => handleNestedFieldChange('businessAddress', 'state', e.target.value)}
                disabled={!isEditing}
                required
              />

              <Input
                label="Postal Code"
                value={formData.businessAddress?.postalCode || ''}
                onChange={(e) => handleNestedFieldChange('businessAddress', 'postalCode', e.target.value)}
                disabled={!isEditing}
                required
              />
            </div>

            <Input
              label={t('common:common.country')}
              value={formData.businessAddress?.country || ''}
              onChange={(e) => handleNestedFieldChange('businessAddress', 'country', e.target.value)}
              disabled={!isEditing}
              required
            />
          </div>
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default CompanyProfilePage;

